import type { OrderDetailsTarget } from '@moment/blotter-feature/order-details-target'
import type { OrderEntryDraftId } from '@moment/horizon-data-access/brands'
import type { MarketplaceTarget } from '@moment/marketplace-feature/target'
import type { ReportId, TemplateId } from '@/reports/model/ids'
import type { TeamTabParam } from '@/teams/constants'
import type { WorkflowSessionId } from '@/workflows/types'

import { serializeMarketplaceFilters } from '@moment/marketplace-feature/target'

export const RISK_GROUP_SEARCH_PARAM = 'riskGroupId'

// Every supplied tab is serialized, including the default one: an absent
// `?tab=` no longer means Order — it means "open the order's default tab",
// which is Executions once the order has filled. Dropping an explicit Order
// target here would silently redirect it.
function blotterDetailPath({
  orderId,
  tab,
  view,
}: { orderId: string } & Partial<OrderDetailsTarget>): string {
  const params = new URLSearchParams()
  if (tab !== undefined) {
    params.set('tab', tab)
  }
  for (const [key, value] of Object.entries(view ?? {})) {
    if (value !== undefined) {
      params.set(key, value)
    }
  }
  const search = params.toString()
  return search === '' ? `/blotter/${orderId}` : `/blotter/${orderId}?${search}`
}

const AGENT_NEW_CHAT_PARAM = 'new'
const AGENT_VOICE_PARAM = 'voice'

interface ChildOrganizationUserDetailParams {
  organizationId: string
  userId: string
}

interface EjHandoffRouteParams {
  custodianAccountId: string
}

export const Routes = {
  Auth: {
    callback: '/auth/callback',
    logout: '/auth/logout',
    logoutEverywhere: '/auth/logout-everywhere',
  },
  PublicApi: {},
  Home: {
    root: '/',
  },
  Help: {
    root: '/help',
  },
  Views: {
    root: '/views',
  },
  WorkflowSessions: {
    root: '/views?entity=workflow_session',
    detail: ({ sessionId }: { sessionId: WorkflowSessionId }): string =>
      `/workflow-sessions/${encodeURIComponent(sessionId)}`,
  },
  Clients: {
    root: '/clients',
    list: '/clients',
  },
  Research: {
    root: '/research',
    discover: '/research/discover',
    fixedIncome: '/research/fixed-income',
    assetClass: (slug: string): string => `/research/${slug}`,
    // Deprecated: Use fixedIncome instead. Kept for backwards compatibility.
    bondScreener: `/research/bond-screener`,
    preview: ({ bondId }: { bondId: string }): string =>
      `/research/fixed-income/${bondId}/preview`,
    detail: ({ bondId }: { bondId: string }): string =>
      `/research/fixed-income/${bondId}`,
  },
  Orders: {
    root: '/orders/trade-blotter',
    tradeBlotter: '/orders/trade-blotter',
    historicalOrders: '/orders/historical-orders',
    riskControls: '/orders/risk-controls',
    preview: ({ orderId }: { orderId: string }): string =>
      `/orders/${orderId}/preview`,
    orderCreate: '/orders/create',
  },
  OrderEntry: {
    root: '/order-entry',
    draft: ({ draftId }: { draftId: OrderEntryDraftId }): string =>
      `/order-entry?${new URLSearchParams({ draft: draftId }).toString()}`,
  },
  Models: {
    root: '/models',
    list: '/models',
    detail: ({ modelId }: { modelId: string }): string => `/models/${modelId}`,
    builder: '/models/new',
    edit: ({ modelId }: { modelId: string }): string =>
      `/models/${modelId}/edit`,
  },
  FilterList: {
    root: '/lists',
    list: '/lists',
    detail: ({
      filterListId,
      version = 0,
    }: {
      filterListId: string
      version?: number
    }): string => `/lists/${filterListId}?version=${version}`,
    create: '/lists/new',
    edit: ({ filterListId }: { filterListId: string }): string =>
      `/lists/${filterListId}/edit`,
  },
  Allocations: {
    root: ({
      allocationId,
      orderId,
    }: {
      allocationId?: string
      orderId?: string
    } = {}): string => {
      if (!allocationId) return '/allocations'
      const params = new URLSearchParams()
      params.set('allocationId', allocationId)
      if (orderId) params.set('orderId', orderId)
      return `/allocations/run?${params.toString()}`
    },
    create: ({
      instrumentId,
      momentSleeveIds,
      momentAccountIds,
      orderId,
    }: {
      instrumentId?: string
      momentSleeveIds?: string[]
      momentAccountIds?: string[]
      orderId?: string
    } = {}): string => {
      const params = new URLSearchParams()
      if (instrumentId) {
        params.set('instrumentId', instrumentId)
      }
      if (momentSleeveIds && momentSleeveIds.length > 0) {
        params.set('momentSleeveIds', momentSleeveIds.join(','))
      }
      if (momentAccountIds && momentAccountIds.length > 0) {
        params.set('momentAccountIds', momentAccountIds.join(','))
      }
      if (orderId) {
        params.set('orderId', orderId)
      }
      const queryString = params.toString()
      return queryString
        ? `/allocations/run?${queryString}`
        : '/allocations/run'
    },
    // detail: ({ allocationId }: { allocationId: string }): string =>
    //   `/allocations/${allocationId}`,
  },
  Accounts: {
    root: '/accounts',
    detail: ({ accountId }: { accountId: string }): string =>
      `/accounts/${accountId}`,
    sleeves: {
      root: ({ accountId }: { accountId: string }): string =>
        `/accounts/${accountId}/sleeves`,
      create: ({ accountId }: { accountId: string }): string =>
        `/accounts/${accountId}/sleeves/create`,
      // Legacy SleeveManagement editor (the "Edit sleeve" flow) on its own
      // route, so the base sleeve path is free for the v3 detail page.
      edit: ({
        accountId,
        sleeveId,
      }: {
        accountId: string
        sleeveId: string
      }): string => `/accounts/${accountId}/sleeves/${sleeveId}/edit`,
      // v3 sleeve detail page lives at the base sleeve path.
      detail: ({
        accountId,
        sleeveId,
      }: {
        accountId: string
        sleeveId: string
      }): string => `/accounts/${accountId}/sleeves/${sleeveId}`,
    },
  },
  TradeDesk: {
    root: '/trade-desk/trade-blotter',
    tradeBlotter: '/trade-desk/trade-blotter',
    historicalOrders: '/trade-desk/historical-orders',
    riskControls: '/trade-desk/risk-controls',
    attestations: '/trade-desk/attestations',
    executionRules: '/trade-desk/execution-rules',
    inquiryPricingRules: '/trade-desk/inquiry-pricing-rules',
    markupEditor: '/markup-editor',
    repFeeEditor: '/rep-fee-editor',
    preview: ({ orderId }: { orderId: string }): string =>
      `/trade-desk/${orderId}/preview`,
    bulkUpload: '/trade-desk/trade-blotter/bulk-upload',
  },
  Blotter: {
    root: '/blotter',
    detail: blotterDetailPath,
    bulkUpload: ({ entryId }: { entryId: string }): string =>
      `/blotter/bulk-upload/${entryId}`,
    bulkUploadFromEmpty: ({ entryId }: { entryId: string }): string =>
      `/blotter/bulk-upload/${entryId}?start=empty`,
    draft: ({ draftId }: { draftId: OrderEntryDraftId }): string =>
      `/blotter?${new URLSearchParams({ draft: draftId }).toString()}`,
    groupOrders: {
      root: '/blotter/group-orders',
      unassigned: '/blotter/group-orders/unassigned',
      eligibility: '/blotter/group-orders/eligibility',
      review: '/blotter/group-orders/review',
    },
  },
  Proposals: {
    root: '/proposals',
    v2: ({ proposalId }: { proposalId: string }): string =>
      `/proposals/v2/${proposalId}`,
    detail: ({ proposalId }: { proposalId: string }): string =>
      `/proposals/${proposalId}`,
    review: ({ proposalId }: { proposalId: string }): string =>
      `/proposals/${proposalId}/review`,
    detailWithSelectedAccount: ({
      proposalId,
      selectedAccount,
    }: {
      proposalId: string
      selectedAccount: string
    }): string =>
      `/proposals/${proposalId}?selectedAccount=${encodeURIComponent(selectedAccount)}`,
    detailWithSelectedAccountAndSleeve: ({
      proposalId,
      selectedAccount,
      selectedSleeve,
    }: {
      proposalId: string
      selectedAccount: string
      selectedSleeve: string
    }): string =>
      `/proposals/${proposalId}?selectedAccount=${encodeURIComponent(selectedAccount)}&selectedSleeve=${encodeURIComponent(selectedSleeve)}`,
  },
  Origination: {
    root: '/views?entity=new_issue',
    detail: ({ newIssueId }: { newIssueId: string }): string =>
      `/origination/${newIssueId}`,
    details: ({ newIssueId }: { newIssueId: string }): string =>
      `/origination/${newIssueId}/details`,
    finalTerms: ({ newIssueId }: { newIssueId: string }): string =>
      `/origination/${newIssueId}/final-terms`,
    preview: ({ newIssueId }: { newIssueId: string }): string =>
      `/views?entity=new_issue&previewNewIssueId=${encodeURIComponent(newIssueId)}`,
  },
  Offerings: {
    root: '/offerings',
  },
  IpoManagement: {
    root: '/ipo-management',
    detail: ({ newIssueId }: { newIssueId: string }): string =>
      `/ipo-management/${newIssueId}`,
  },
  Marketplace: {
    root: '/marketplace',
    /** Opens a view with `filters` on top of it as unsaved changes the user can save. */
    view: ({
      viewId,
      filters,
      riskGroupId,
    }: MarketplaceTarget & { riskGroupId?: string }): string => {
      const params = new URLSearchParams()
      if (viewId !== null) {
        params.set('view', viewId)
      }
      if (filters.length > 0) {
        params.set('filters', serializeMarketplaceFilters(filters))
      }
      if (riskGroupId) {
        params.set(RISK_GROUP_SEARCH_PARAM, riskGroupId)
      }
      const search = params.toString()
      return search === '' ? '/marketplace' : `/marketplace?${search}`
    },
    /** Opens the instrument pane; `view` and `filters` restore the view it was expanded from. */
    instrument: (
      securityId: string,
      {
        view,
        filters,
        riskGroupId,
      }: {
        view?: string | null
        filters?: string | null
        riskGroupId?: string
      } = {},
    ): string => {
      const params = new URLSearchParams({ instrument: securityId })
      if (view) {
        params.set('view', view)
      }
      if (filters) {
        params.set('filters', filters)
      }
      if (riskGroupId) {
        params.set(RISK_GROUP_SEARCH_PARAM, riskGroupId)
      }
      return `/marketplace?${params}`
    },
  },
  Instruments: {
    /** `from` lets the page minimize back; `view` and `filters` are the marketplace view to return to. */
    detail: (
      securityId: string,
      {
        from,
        view,
        filters,
        riskGroupId,
      }: {
        from?: 'marketplace'
        view?: string | null
        filters?: string | null
        riskGroupId?: string
      } = {},
    ): string => {
      const path = `/instruments/${encodeURIComponent(securityId)}`
      if (from === undefined) {
        return path
      }
      const params = new URLSearchParams({ from })
      if (view) {
        params.set('view', view)
      }
      if (filters) {
        params.set('filters', filters)
      }
      if (riskGroupId) {
        params.set(RISK_GROUP_SEARCH_PARAM, riskGroupId)
      }
      return `${path}?${params}`
    },
  },
  IpoMarketplace: {
    root: '/ipo-marketplace',
    detail: ({ newIssueId }: { newIssueId: string }): string =>
      `/ipo-marketplace/${newIssueId}`,
  },
  TradePlan: {
    detail: ({ tradePlanId }: { tradePlanId: string }): string =>
      `/trade-plan/${tradePlanId}`,
  },
  Education: {
    root: '/education',
    courses: '/education/courses',
    detail: ({ requirementId }: { requirementId: string }): string =>
      `/education/${requirementId}`,
    module: ({
      requirementId,
      moduleSlug,
    }: {
      requirementId: string
      moduleSlug: string
    }): string => `/education/${requirementId}/${moduleSlug}`,
  },
  Settings: {
    root: '/settings/users',
    users: '/settings/users',
    userDetail: (userId: string): string => `/settings/users/${userId}`,
    teams: '/settings/teams',
    objectGroups: '/settings/object-groups',
    objectGroupDetail: (objectGroupId: string): string =>
      `/settings/object-groups/${objectGroupId}`,
    roles: '/settings/roles',
    tags: '/settings/tags',
    profile: '/settings/profile',
    traderProfile: '/settings/profile/trader-profile',
    organizations: '/settings/organizations',
    childOrganization: (organizationId: string): string =>
      `/settings/organizations/${organizationId}`,
    childOrganizationUsers: (organizationId: string): string =>
      `/settings/organizations/${organizationId}/users`,
    childOrganizationUserDetail: ({
      organizationId,
      userId,
    }: ChildOrganizationUserDetailParams): string =>
      `/settings/organizations/${organizationId}/users/${userId}`,
    childOrganizationRoles: (organizationId: string): string =>
      `/settings/organizations/${organizationId}/roles`,
  },
  Agent: {
    root: '/agent',
    chat: ({ runId }: { runId: string }): string =>
      `/agent/${encodeURIComponent(runId)}`,
    newChatParam: AGENT_NEW_CHAT_PARAM,
    // A fresh nonce each time, so the page remounts even when it is already
    // showing a new chat.
    newChat: (): string =>
      `/agent?${AGENT_NEW_CHAT_PARAM}=${crypto.randomUUID()}`,
    voiceParam: AGENT_VOICE_PARAM,
    /** A new chat that turns voice mode on as it opens. */
    newVoiceChat: (): string =>
      `/agent?${AGENT_NEW_CHAT_PARAM}=${crypto.randomUUID()}&${AGENT_VOICE_PARAM}=1`,
  },
  FixedIncomeHub: {
    root: '/fixed-income-hub',
    run: ({ runId }: { runId: string }): string =>
      `/fixed-income-hub/runs/${encodeURIComponent(runId)}`,
    session: ({ sessionId }: { sessionId: string }): string =>
      `/fixed-income-hub/sessions/${encodeURIComponent(sessionId)}`,
    ejHandoff: ({ custodianAccountId }: EjHandoffRouteParams): string =>
      `/fixed-income-hub/ej-handoff?custodian_account_id=${encodeURIComponent(custodianAccountId)}`,
  },
  AI: {
    root: '/ai',
    controls: '/ai/controls',
  },
  Strategies: {
    root: '/strategies',
    detail: ({ strategyId }: { strategyId: string }): string =>
      `/strategies/${strategyId}`,
    edit: ({ strategyId }: { strategyId: string }): string =>
      `/strategies/${strategyId}/edit`,
    new: {
      // `/strategies/new/security-list` 308s here (next.config.js + leftover page).
      securityLevel: '/strategies/new/security-level',
      fixedIncome: '/strategies/new/fixed-income',
    },
  },
  AccountGroups: {
    root: '/views?entity=account_group',
    detail: ({ accountGroupId }: { accountGroupId: string }): string =>
      `/account-groups/${accountGroupId}`,
  },
  Teams: {
    root: '/settings/teams',
    detail: ({
      teamId,
      tab,
    }: {
      teamId: string
      tab?: TeamTabParam
    }): string => {
      const path = `/settings/teams/${teamId}`
      return tab === undefined
        ? path
        : `${path}?${new URLSearchParams({ tab })}`
    },
  },
  AllocationTemplates: {
    root: '/views?entity=asset_allocation_template',
    new: '/allocation-templates/new',
    newLinked: ({ baseTemplateId }: { baseTemplateId: string }): string =>
      `/allocation-templates/new?base=${encodeURIComponent(baseTemplateId)}`,
    detail: ({ templateId }: { templateId: string }): string =>
      `/allocation-templates/${templateId}`,
    edit: ({ templateId }: { templateId: string }): string =>
      `/allocation-templates/${templateId}/edit`,
  },
  Reports: {
    root: '/reports',
    builder: ({ templateId }: { templateId: TemplateId }): string =>
      `/reports/builder/${templateId}`,
    export: ({ reportId }: { reportId: ReportId }): string =>
      `/reports/export/${reportId}`,
  },
  Alerts: {
    root: '/alerts',
  },
  ExternalQuotes: {
    root: '/external-quotes',
  },
  CustomApps: {
    root: '/custom-apps',
    detail: ({ appKey }: { appKey: string }): string =>
      `/custom-apps/${appKey}`,
  },
  Login: {
    root: '/login',
  },
  Logout: {
    root: '/logout',
  },
  Forbidden: '/403',
}
